import type React from "react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import Navbar from "../components/Navbar"
import { useAuth } from "../context/AuthContext"
import api from "../utils/api"
import "./PublishProduct.css"

const PublishProduct: React.FC = () => {
  const [productName, setProductName] = useState("")
  const [price, setPrice] = useState("")
  const [location, setLocation] = useState("")
  const [description, setDescription] = useState("")
  const [category, setCategory] = useState("")
  const [customCategory, setCustomCategory] = useState("")
  const [images, setImages] = useState<File[]>([])
  const [previewUrls, setPreviewUrls] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const { user } = useAuth()
  const navigate = useNavigate()

  const predefinedCategories = ["Top", "Bottom", "Accessories", "Shoes", "Vest", "Dress", "Others"]

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCategory = e.target.value
    setCategory(selectedCategory)
    if (selectedCategory !== "Others") {
      setCustomCategory("")
    }
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files)
      const newPreviews = filesArray.map((file) => URL.createObjectURL(file))

      setImages((prev) => [...prev, ...filesArray])
      setPreviewUrls((prev) => [...prev, ...newPreviews])
    }
  }

  const removeImage = (index: number) => {
    URL.revokeObjectURL(previewUrls[index])
    setImages((prev) => prev.filter((_, i) => i !== index))
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!productName || !price || !location || !description) {
      setError("Please fill in all required fields")
      return
    }

    try {
      setLoading(true)
      setError("")

      const formData = new FormData()
      formData.append("name", productName)
      formData.append("price", price)
      formData.append("pickup_location", location)
      formData.append("description", description)

      // Use custom category if "Others" is selected, otherwise use selected category
      const finalCategory = category === "Others" ? customCategory : category
      formData.append("category", finalCategory)

      images.forEach((image) => {
        formData.append("images", image)
      })

      await api.post("/products/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })

      navigate("/your-products")
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to publish product")
      console.error("Error publishing product:", err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Navbar />
      <div className="publish-product-container">
        <div className="container">
          <div className="publish-product-content">
            <div className="page-header">
              <h1 className="page-title">Publish Your Product</h1>
              <p className="page-subtitle">Share your item with the community and start selling today</p>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="publish-form">
              <div className="form-section">
                <h3 className="section-title">Basic Information</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="productName" className="form-label">
                      Product Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="productName"
                      className="form-input"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="Enter a descriptive product name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="price" className="form-label">
                      Price (RM) <span className="required">*</span>
                    </label>
                    <div className="price-input-container">
                      <input
                        type="number"
                        id="price"
                        className="form-input price-input"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category" className="form-label">
                      Category
                    </label>
                    <div className="select-container">
                      <select id="category" className="form-select" value={category} onChange={handleCategoryChange}>
                        <option value="">Select a category</option>
                        {predefinedCategories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                      
                    </div>
                  </div>

                  {category === "Others" && (
                    <div className="form-group">
                      <label htmlFor="customCategory" className="form-label">
                        Custom Category
                      </label>
                      <input
                        type="text"
                        id="customCategory"
                        className="form-input"
                        value={customCategory}
                        onChange={(e) => setCustomCategory(e.target.value)}
                        placeholder="Enter your custom category"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="location" className="form-label">
                      Pickup Location <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="location"
                      className="form-input"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Enter pickup location (e.g., Kuala Lumpur, Selangor)"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">Product Description</h3>

                <div className="form-group">
                  <label htmlFor="description" className="form-label">
                    Description <span className="required">*</span>
                  </label>
                  <textarea
                    id="description"
                    className="form-textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your product in detail. Include condition, features, and any important information buyers should know."
                    rows={6}
                    required
                  />
                  <div className="character-count">{description.length}/500 characters</div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="section-title">Product Images</h3>

                <div className="form-group">
                  <label className="form-label">Upload Photos</label>
                  <p className="form-helper">Add up to 10 photos to showcase your product</p>

                  <div className="upload-container">
                    <div className="upload-area">
                      <div className="upload-content">
                        <div className="upload-icon">📷</div>
                        <p className="upload-text">Click to browse or drag and drop</p>
                        <p className="upload-subtext">PNG, JPG, JPEG up to 10MB each</p>
                      </div>
                      <input
                        type="file"
                        id="images"
                        onChange={handleImageChange}
                        multiple
                        accept="image/*"
                        className="file-input"
                      />
                    </div>

                    {previewUrls.length > 0 && (
                      <div className="image-previews">
                        <h4 className="preview-title">Uploaded Images ({previewUrls.length})</h4>
                        <div className="preview-grid">
                          {previewUrls.map((url, index) => (
                            <div key={index} className="image-preview">
                              <img src={url || "/placeholder.svg"} alt={`Preview ${index + 1}`} />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="remove-image-btn"
                                title="Remove image"
                              >
                                ✕
                              </button>
                              {index === 0 && <div className="primary-badge">Primary</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => navigate("/")} className="cancel-btn" disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="publish-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Publishing...
                    </>
                  ) : (
                    <>
                      <span className="publish-icon">🚀</span>
                      Publish Product
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default PublishProduct
